import { ChevronLeft, ChevronRight } from 'lucide-react'

import { cn } from '@/utils/cn'

interface PaginationProps {
  page: number
  totalPages: number
  totalCount: number
  pageSize: number
  onPageChange: (page: number) => void
}

/** Gera a sequência de páginas com reticências: 1 … 4 [5] 6 … 18 */
function pageWindow(page: number, totalPages: number): (number | 'gap')[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
  const pages: (number | 'gap')[] = [1]
  const start = Math.max(2, page - 1)
  const end = Math.min(totalPages - 1, page + 1)
  if (start > 2) pages.push('gap')
  for (let i = start; i <= end; i += 1) pages.push(i)
  if (end < totalPages - 1) pages.push('gap')
  pages.push(totalPages)
  return pages
}

export function Pagination({
  page,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
}: PaginationProps) {
  if (totalCount === 0) return null
  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, totalCount)
  const safeTotalPages = Math.max(totalPages, 1)

  return (
    <nav
      className="flex flex-col items-center justify-between gap-3 sm:flex-row"
      aria-label="Paginação"
    >
      <p className="text-sm text-fg-muted">
        Mostrando <span className="font-medium text-fg">{from}</span>–
        <span className="font-medium text-fg">{to}</span> de{' '}
        <span className="font-medium text-fg">{totalCount}</span>
      </p>

      <div className="flex items-center gap-1">
        <button
          type="button"
          className="grid size-9 place-items-center rounded-lg border border-line text-fg-muted disabled:opacity-40 hover:enabled:bg-surface-2"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Página anterior"
        >
          <ChevronLeft className="size-4" />
        </button>

        {pageWindow(page, safeTotalPages).map((item, index) =>
          item === 'gap' ? (
            <span key={`gap-${index}`} className="px-1.5 text-fg-subtle">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => onPageChange(item)}
              aria-current={item === page ? 'page' : undefined}
              className={cn(
                'grid size-9 place-items-center rounded-lg border text-sm',
                item === page
                  ? 'border-brand bg-brand text-white'
                  : 'border-line text-fg-muted hover:bg-surface-2',
              )}
            >
              {item}
            </button>
          ),
        )}

        <button
          type="button"
          className="grid size-9 place-items-center rounded-lg border border-line text-fg-muted disabled:opacity-40 hover:enabled:bg-surface-2"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= safeTotalPages}
          aria-label="Próxima página"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
    </nav>
  )
}
